import React, { useEffect, useState, useCallback } from "react";
import { Box } from "@mui/material";
import { styled } from "@mui/system";

import { Card, CardHeader, CardContent, Avatar, ForgeIcon, CheckIcon, CloseCircleIcon, Button } from "./ui";

import config from "../utils/config.json";

import { logout } from "../services/authService";

// Styled components
const MessageContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "flex-start",
  padding: theme.spacing(1),
  // marginLeft: '20%',
  [theme.breakpoints.down("lg")]: {
    marginLeft: 0,
    padding: theme.spacing(1),
  },
}));

const ButtonContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  marginTop: "6px",
  gap: theme.spacing(2), // Space between buttons
}));

const ChatLegalStatements = ({
  index,
  language,
  msgEnabled,
  setMsgEnabled,
  postsArray,
  setPostsArray,
}) => {
  //React state determining which legal notice to render
  const [legalNotice, setLegalNotice] = React.useState("disclaimer");
  let title;
  let body;
  let bodyConfirm;
  if (legalNotice === "disclaimer") {
    title = config.languages.find((lang) => lang.code === language).texts
      .legalDisclaimerTitle;
    body = config.languages.find((lang) => lang.code === language).texts
      .legallong;
    bodyConfirm = config.languages.find((lang) => lang.code === language).texts
      .legallongConfirm;
  }
  //else if(legalNotice === 'eula'){
  //  title = 'End-User License Agreement'
  //  body = "Hi, I'm a placeholder EULA, you just got EULA'd."
  //}
  //else{
  //  title = "How'd you get here?"
  //  body = "No, seriously, this should be unreachable"
  //}
  else {
    //If it's not a disclaimer, make it a disclaimer
    setLegalNotice("disclaimer");
  }

  //Bunch of react states for the confirm and deny buttons
  const [buttonsDisabledLegal, setButtonsDisabledLegal] = React.useState(false);
  // For confirm/deny, there are three states
  //
  //  confirmButtonText === Actual text in the confirm button -- should change from 'Confirm' to 'Confirmed' on confirm button click
  //  confirmButtonVisible === Whether the confirm button is actually visible or not. Defaults to true, set to false when deny button is clicked
  const [confirmButtonText, setConfirmButtonText] = React.useState(
    config.languages.find((lang) => lang.code === language).texts.confirm
  );
  const [confirmButtonVisible, setConfirmButtonVisible] = React.useState(true);
  const [denyButtonText, setDenyButtonText] = React.useState(
    config.languages.find((lang) => lang.code === language).texts.cancel
  );
  const [denyButtonVisible, setDenyButtonVisible] = React.useState(true);

  // Add react state that corresponds to whether or not the Legal Disclaimer record on Cognito shows an 'accept'
  // This is important, because if the Cognito record does not show an 'accept', then the Lambda will bounce off NLQ requests for this Cognito account
  const [disclaimerConfirmed, setDisclaimerConfirmed] = useState(false);
  const [localDisclaimerConfirmed, setLocalDisclaimerConfirmed] =
    useState(false);
  //Replaced react states with trivial constants to prevent these checks
  //const [ eulaConfirmed, setEulaConfirmed ] = useState(false);
  //const [ localEulaConfirmed, setLocalEulaConfirmed ] = useState(false);
  const eulaConfirmed = true;

  // const setEulaConfirmed = () => {};

  const localEulaConfirmed = true;
  const setLocalEulaConfirmed = () => {};

  // We do NOT want this to be asynchronous -- we are WAITING on the value of this function
  const check_legal_confirmed = useCallback(() => {
    // console.log("DEBUG check_legal \r\nDisclaimer Confirmed: " + disclaimerConfirmed +
    //   "\tEULA Confirmed: " + eulaConfirmed);

    if (!disclaimerConfirmed || !eulaConfirmed) {
      let disclaimerStatus;
      let eulaStatus;

      //const cognitoClient = new CognitoIdentityProviderClient({
      //    region: config.region,
      //});

      // Instead of checking with Cognito + access token, call a lambda function
      // Send disclaimer acceptance to Cognito
      const header = {
        Accept: "application/json",
        "Content-Type": "application/json",
        cloudtoken: sessionStorage.getItem("idToken"),
        cloudtoken2: sessionStorage.getItem("accessToken"),
      };
      const body = JSON.stringify({
        requestType: "get",
        accept: true,
        legalStatement: legalNotice,
      });
      const requestUrl = window.apiURLlegal;
      //const params = { "AccessToken" : sessionStorage['accessToken'] };
      //const command = new GetUserCommand(params);
      //cognitoClient.send(command).then((response) => {
      fetch(requestUrl, {
        method: "POST",
        mode: "cors",
        headers: header,
        body: body,
      })
        .then((response) => response.json())
        .then((response) => {
          // Find the value of the EULA date by looping through all of the listed attributes (there is no quicker way to do this)
          response["UserAttributes"].forEach((attribute) => {
            if (attribute["Name"] === "custom:eula_date") {
              eulaStatus = attribute["Value"];
            } else if (attribute["Name"] === "custom:disclaimer_date") {
              disclaimerStatus = attribute["Value"];
            }
          });

          // console.log("DEBUG Eula Status From Cognito: " + eulaStatus);
          // console.log("DEBUG Disclaimer Status From Cognito: " + disclaimerStatus);

          return [eulaStatus, disclaimerStatus];
        })
        .then((statusArray) => {
          // if ((eulaStatus != null) && (eulaStatus.includes("accepted"))) {
          //     // Don't update unless we're changing the react state
          //     if(!eulaConfirmed){
          //         setEulaConfirmed(true);
          //     }
          // } else {
          //     // Don't update unless we're changing the react state
          //     if(eulaConfirmed){
          //         setEulaConfirmed(false);
          //     }
          // }

          if (
            disclaimerStatus != null &&
            disclaimerStatus.includes("accepted")
          ) {
            // Don't update unless we're changing the react state
            if (!disclaimerConfirmed) {
              setDisclaimerConfirmed(true);
            }
          } else {
            // Don't update unless we're changing the react state
            if (disclaimerConfirmed) {
              setDisclaimerConfirmed(false);
            }
          }
        })
        .catch((error) => {
          console.log("Error In Accessing AWS Cognito");
          console.log(error);

          if (error.name.includes("NotAuthorizedException")) {
            logout(error);
          }
        });
    }
  }, [disclaimerConfirmed, eulaConfirmed, legalNotice]);
  // Set the interval to check periodically
  const [legalIntervalRunning, setLegalIntervalRunning] = useState(false);

  // An effect to monitor disclaimerConfirmed and localDisclaimerConfirmed
  useEffect(() => {
    //Runs on the first render
    //And any time any dependency value changes
    // if (!(disclaimerConfirmed || localDisclaimerConfirmed || eulaConfirmed || localEulaConfirmed)) {
    //   if (sessionStorage.getItem("disclaimerPastStatus").includes("accepted") && sessionStorage.getItem("eulaPastStatus").includes("accepted")) {
    //     console.log("Both Legal Statements Are Logged In Cognito As Approved");
    //   }
    // }

    // Actual logic

    if (localDisclaimerConfirmed ^ localEulaConfirmed) {
      check_legal_confirmed();
    }

    if (
      disclaimerConfirmed &&
      localDisclaimerConfirmed &&
      eulaConfirmed &&
      localEulaConfirmed
    ) {
      setMsgEnabled(true);

      // Remove the legal disclaimer on both true
      // Put another way, this element deletes itself
      setPostsArray((postsArray) => [
        ...postsArray.slice(0, index),
        ...postsArray.slice(index + 1),
      ]);

      setLegalIntervalRunning(false);
      //console.log(checkDisclaimerInterval);
    }
  }, [
    index,
    disclaimerConfirmed,
    localDisclaimerConfirmed,
    eulaConfirmed,
    localEulaConfirmed,
    setMsgEnabled,
    postsArray,
    setPostsArray,
    check_legal_confirmed,
  ]);

  // We use this effect to manage the interval
  // By wrapping the interval entirely in this effect, we spawn a single interval, instead of many
  useEffect(() => {
    if (legalIntervalRunning) {
      const checkLegalInterval = setInterval(() => {
        // check legal disclaimer on every interval
        check_legal_confirmed();
        //console.log("DEBUG Checked Disclaimer Confirmation Occurence: " + disclaimerConfirmed);
        //console.log("DEBUG Checked Local Disclaimer Confirmation Occurence: " + localDisclaimerConfirmed);
      }, 250);

      return () => {
        clearInterval(checkLegalInterval);
      };
    }
  }, [legalIntervalRunning, check_legal_confirmed]);

  async function confirm_legal(event) {
    // This function should activate on legal confirm button click
    //  It will then send up a post to Cognito to update the EULA accept, so the Interval can detect on accept on the Cognito side
    //  Then, localDisclaimerConfirm will be updated to true, so that an effect can pick up the button click
    // This will trigger an effect that looks at whether both disclaimerConfirmed (on Cognito) and localDisclaimerConfirmed (on client-side) are true -- if so, it will alter a boolean that will trigger interval clean-up

    // Add functionality to enable message bar and send to Cognito here
    const nextSibling = event.target.nextSibling;
    if (nextSibling) {
      nextSibling.disabled = true;
    }

    // Send disclaimer acceptance to Cognito
    const header = {
      Accept: "application/json",
      "Content-Type": "application/json",
      cloudtoken: sessionStorage.getItem("idToken"),
      cloudtoken2: sessionStorage.getItem("accessToken"),
    };
    const body = JSON.stringify({
      requestType: "set",
      accept: true,
      legalStatement: legalNotice,
    });
    const requestUrl = window.apiURLlegal;

    // We DO want the await here
    // If we don't wait for this post to resolve, we will start checking Cognito before our POST promise has been fulfilled
    await fetch(requestUrl, {
      method: "POST",
      mode: "cors",
      headers: header,
      body: body,
    }).catch((error) => {
      console.error("error:", error);
    });

    if (legalNotice === "disclaimer") {
      setLocalDisclaimerConfirmed(true);
      if (!localEulaConfirmed) {
        //Switch states && re-enable buttons
        setLegalNotice("eula");
        setConfirmButtonText(
          config.languages.find((lang) => lang.code === language).texts.confirm
        );
        setDenyButtonVisible(true);
        setButtonsDisabledLegal(false);
      } else {
        check_legal_confirmed();
        setLegalIntervalRunning(true);
      }
    } else if (legalNotice === "eula") {
      setLocalEulaConfirmed(true);
      if (!localDisclaimerConfirmed) {
        //Switch states && re-enable buttons
        setLegalNotice("disclaimer");
        setConfirmButtonText(
          config.languages.find((lang) => lang.code === language).texts.confirm
        );
        setDenyButtonVisible(true);
        setButtonsDisabledLegal(false);
      } else {
        check_legal_confirmed();
        setLegalIntervalRunning(true);
      }
    }

    //Load up welcome message
    const welcomePost = {
      naturalLanguageQuery: "N/A",
      classification: "welcome",
      response: "N/A",
    };
    setPostsArray([...postsArray, welcomePost]);
    // console.log(index);
  }

  const handleConfirm = (event) => {
    if (!buttonsDisabledLegal) {
      setConfirmButtonText(
        config.languages.find((lang) => lang.code === language).texts.confirmed
      );
      setDenyButtonVisible(false);
      confirm_legal(event);
      setButtonsDisabledLegal(true);
    }
  };

  async function deny_legal(event) {
    // Add functionality to disable message bar and send to Cognito here
    const previousSibling = event.target.previousSibling;
    if (previousSibling) {
      previousSibling.disabled = true;
    }
    // Actual logic
    setLocalDisclaimerConfirmed(false);
    setMsgEnabled(false); //Double-check message bar is disabled

    // Send to rejection to Cognito
    const header = {
      Accept: "application/json",
      "Content-Type": "application/json",
      cloudtoken: sessionStorage.getItem("idToken"),
      cloudtoken2: sessionStorage.getItem("accessToken"),
    };
    const body = JSON.stringify({
      requestType: "set",
      accept: false,
      legalStatement: legalNotice,
    });
    const requestUrl = window.apiURLlegal;

    await fetch(requestUrl, {
      method: "POST",
      mode: "cors",
      headers: header,
      body: body,
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        console.error("error:", error);
      });

    logout();
  }

  const handleDeny = (event) => {
    if (!buttonsDisabledLegal) {
      setDenyButtonText(
        config.languages.find((lang) => lang.code === language).texts.cancelled
      );
      setConfirmButtonVisible(false);
      deny_legal(event);
      setButtonsDisabledLegal(true);
    }
  };

    const [smMargin, setSmMargin] = React.useState('20%');
    React.useEffect(() => {
      if (msgEnabled) {
        setSmMargin('0%');
      } else {
        setSmMargin('20%');
      }
    }, [msgEnabled]); 

    return(
        <MessageContainer aria-live='polite'>
            <Card
                className="mx-auto mt-6 w-[70vw] min-w-full max-w-4xl"
                >
                <CardHeader
                    avatar={<Avatar icon={<ForgeIcon />} size="md"/>}
                    className="items-center"
                    label=""
                    subtitle="HelpBot"
                    title="Generative AI Legal Disclaimer"
                    titleVariant="default"
                />
                <CardContent top="none">
                   <p>
                    This chatbot uses generative artificial intelligence and employs third party large language models (LLMs). Honeywell is providing this chatbot feature for the sole purpose of allowing you to submit Software-related queries and should not be used for any other purpose. The LLMs are enhanced through retrieval augmented generation (RAG) based on Honeywell's Documentation and specifications to generate the outputs. The information or output provided by the chatbot feature may not be accurate, complete or up-to-date and may be misleading or contain errors and omissions, or the chatbot feature may misunderstand the question or input and may be responding to a different question than asked. The information and outputs provided by the chatbot feature are intended for general informational purposes in the United States only. All information provided by the chatbot feature includes a reference to the Honeywell source documentation and specifications; always consult the referenced Honeywell documentation and specifications to verify the information provided by the chatbot feature and do not rely on outputs or information generated by the chatbot feature without reviewing, verifying, and testing the output to ensure that it is accurate, error-free, appropriate, and suitable for the intended purpose. You should not use any information provided by the chatbot feature as the basis for making any legal, safety, health or regulatory decision. Further, you are responsible for your input to the chatbot feature and the output generated and returned by the chatbot feature based on the input, including, but not limited to, ensuring that the input and output do not violate any applicable laws or the Honeywell End User License Agreement. You acknowledge and agree that your input may not be kept confidential, and your input and output may be used to further enhance the Software. Further, due to the nature of machine learning and generative artificial intelligence, the output may not be unique across users and the chatbot feature may generate or return the same or similar output to Honeywell or a third party, and other users may also ask similar questions and receive the same response.
                   </p>
                   <br />
                   <em>
                   By clicking “Confirm” or using this chatbot feature, you acknowledge and agree to this legal disclaimer and the Honeywell End User License Agreement linked below.
                   </em>
                   <ButtonContainer>
                    <Button
                        icon={<CheckIcon strokeWidth={2} />}  
                        iconPosition="left"
                        onClick={handleConfirm}
                        size="regular"
                        variant="secondary-fill"
                    >
                        Confirm
                    </Button>

                     <Button
                        icon={<CloseCircleIcon strokeWidth={2} />}
                        iconPosition="left"
                        onClick={handleDeny}
                        size="regular"
                        variant="destructive-secondary"
                    >
                        Cancel
                     </Button>
                   </ButtonContainer>
                </CardContent>
                </Card>
        </MessageContainer>
    )
  };

export default ChatLegalStatements;
